<script setup>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import VoiceAiConfigAPI from 'dashboard/api/voiceAiConfig';

const store = useStore();
const isAdmin = computed(() => store.getters.getCurrentRole === 'administrator');

// ---- ERP skills upload ----
const skillFile = ref(null);
const skillMode = ref('replace');
const skillUploading = ref(false);
const skillStatus = ref({ state: 'idle' });
let skillPoll = null;

const onSkillFile = e => {
  skillFile.value = e.target.files?.[0] || null;
};
const refreshSkillStatus = async () => {
  try {
    const { data } = await VoiceAiConfigAPI.skillsStatus();
    skillStatus.value = data || { state: 'idle' };
    // stop polling once the processor finished
    if (['done', 'error', 'idle'].includes(skillStatus.value.state) && skillPoll) {
      clearInterval(skillPoll);
      skillPoll = null;
    }
  } catch (e) {
    /* ignore */
  }
};
const startSkillPolling = () => {
  if (skillPoll) clearInterval(skillPoll);
  skillPoll = setInterval(refreshSkillStatus, 4000);
};
const uploadSkills = async () => {
  if (!skillFile.value) {
    useAlert('Choose a .zip of skill files first.');
    return;
  }
  skillUploading.value = true;
  try {
    await VoiceAiConfigAPI.uploadSkills(skillFile.value, skillMode.value);
    skillStatus.value = { state: 'queued', detail: 'upload received' };
    skillFile.value = null;
    startSkillPolling();
    useAlert('Skills uploaded — building the index and reloading the agent…');
  } catch (e) {
    useAlert(e.response?.data?.error || 'Skills upload failed.');
  } finally {
    skillUploading.value = false;
  }
};
onMounted(refreshSkillStatus);
onUnmounted(() => {
  if (skillPoll) clearInterval(skillPoll);
});

const loading = ref(true);
const saving = ref(false);
const assistants = ref([]);
const inboxes = ref([]);
const sessionModes = ref(['link', 'call']);
const controlMethods = ref(['none', 'cobrowse', 'anydesk']);
const controlLabels = {
  none: 'Guide only',
  cobrowse: 'Co-browse',
  anydesk: 'AnyDesk',
};

const form = reactive({
  enabled: false,
  captain_assistant_id: null,
  session_mode: 'link',
  inbox_ids: [],
  inbox_modes: {},
  inbox_controls: {},
  openai_api_key: '',
});
const openaiKeySet = ref(false);
const openaiKeyHint = ref('');

// The AI only works when enabled AND an assistant is chosen AND at least one inbox is selected.
const isActive = computed(
  () =>
    form.enabled &&
    !!form.captain_assistant_id &&
    (form.inbox_ids?.length || 0) > 0
);

const load = async () => {
  loading.value = true;
  try {
    const [{ data: opts }, { data: cfg }] = await Promise.all([
      VoiceAiConfigAPI.formOptions(),
      VoiceAiConfigAPI.get(),
    ]);
    assistants.value = opts.assistants || [];
    inboxes.value = opts.inboxes || [];
    sessionModes.value = opts.session_modes || ['link', 'call'];
    controlMethods.value = cfg.control_methods || ['none', 'cobrowse', 'anydesk'];
    form.enabled = !!cfg.enabled;
    form.captain_assistant_id = cfg.captain_assistant_id || null;
    form.session_mode = cfg.session_mode || 'link';
    form.inbox_ids = (cfg.inbox_ids || []).map(Number);
    form.inbox_modes = { ...(cfg.inbox_modes || {}) };
    form.inbox_controls = { ...(cfg.inbox_controls || {}) };
    openaiKeySet.value = !!cfg.openai_api_key_set;
    openaiKeyHint.value = cfg.openai_api_key_hint || '';
    form.openai_api_key = '';
  } catch (e) {
    useAlert('Could not load Voice AI settings.');
  } finally {
    loading.value = false;
  }
};

onMounted(load);

const toggleInbox = id => {
  const i = form.inbox_ids.indexOf(id);
  if (i === -1) {
    form.inbox_ids.push(id);
    if (!form.inbox_modes[id]) form.inbox_modes[id] = form.session_mode || 'link';
    if (!form.inbox_controls[id]) form.inbox_controls[id] = 'none';
  } else {
    form.inbox_ids.splice(i, 1);
    delete form.inbox_modes[id];
    delete form.inbox_controls[id];
  }
};

const inboxMode = id => form.inbox_modes[id] || form.session_mode || 'link';
const setInboxMode = (id, mode) => {
  form.inbox_modes[id] = mode;
};
const inboxControl = id => form.inbox_controls[id] || 'none';
const setInboxControl = (id, method) => {
  form.inbox_controls[id] = method;
};

const save = async () => {
  saving.value = true;
  try {
    await VoiceAiConfigAPI.update({
      enabled: form.enabled,
      captain_assistant_id: form.captain_assistant_id,
      session_mode: form.session_mode,
      inbox_ids: form.inbox_ids,
      inbox_modes: form.inbox_modes,
      inbox_controls: form.inbox_controls,
      // only sent when the admin typed a new key; blank = keep the stored one
      openai_api_key: form.openai_api_key,
    });
    // reflect the newly-saved key state without exposing the raw value
    if (form.openai_api_key) {
      openaiKeySet.value = true;
      openaiKeyHint.value = `••••${form.openai_api_key.slice(-4)}`;
      form.openai_api_key = '';
    }
    useAlert('Voice AI settings saved.');
  } catch (e) {
    useAlert(e.response?.data?.error || 'Could not save Voice AI settings.');
  } finally {
    saving.value = false;
  }
};
</script>

<template>
  <div class="py-6 w-full">
    <h1 class="text-xl font-semibold text-n-slate-12 mb-1">Voice AI</h1>
    <p class="text-sm text-n-slate-11 mb-5">
      An AI voice agent that calls/connects customers, speaks in Arabic, and answers
      from a Captain assistant's FAQs. It does nothing until you enable it and choose
      the inbox(es) it may work on — just like Captain.
    </p>

    <p v-if="!isAdmin" class="text-sm text-n-slate-11">
      Only administrators can manage Voice AI settings.
    </p>

    <template v-else-if="loading">
      <p class="text-sm text-n-slate-10">Loading…</p>
    </template>

    <template v-else>
      <div class="rounded-xl border border-n-weak p-5 space-y-5">
        <!-- Enable -->
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input v-model="form.enabled" type="checkbox" class="size-4" />
          <span class="font-medium">Enable Voice AI</span>
        </label>

        <!-- Assistant (FAQs source) -->
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            Knowledge (Captain assistant)
          </label>
          <select
            v-model="form.captain_assistant_id"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12"
          >
            <option :value="null">— Select an assistant —</option>
            <option v-for="a in assistants" :key="a.id" :value="a.id">
              {{ a.name }}
            </option>
          </select>
          <p class="mt-1 text-xs text-n-slate-11">
            The AI answers from this assistant's FAQs and glossary.
          </p>
        </div>

        <!-- OpenAI API key used by the voice agent (per this account). Stored securely; never shown back. -->
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            OpenAI API Key
          </label>
          <input
            v-model="form.openai_api_key"
            type="password"
            autocomplete="off"
            :placeholder="openaiKeySet ? `A key is set (${openaiKeyHint}) — leave blank to keep it` : 'sk-...'"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12"
          />
          <p class="mt-1 text-xs text-n-slate-11">
            The voice agent uses this key to talk to OpenAI. Leave blank to keep the current key (or to
            use the server's default key). It is stored securely and never shown again.
          </p>
        </div>

        <!-- ERP Skills upload: a .zip of markdown skill docs the agent answers/acts from -->
        <div class="rounded-lg border border-n-weak p-3 bg-n-solid-1">
          <label class="block text-sm font-medium text-n-slate-12 mb-1">ERP Skills (agent knowledge)</label>
          <p class="text-xs text-n-slate-11 mb-2">
            Upload a <strong>.zip of markdown (.md) skill files</strong>. The agent reads them to answer
            and act on this ERP. It's installed on this server, the search index is rebuilt, and the
            agent reloads automatically — no server access needed.
          </p>
          <div class="flex flex-wrap items-center gap-3">
            <input type="file" accept=".zip" class="text-sm text-n-slate-12" @change="onSkillFile" />
            <label class="flex items-center gap-1 text-xs text-n-slate-11">
              <input v-model="skillMode" type="radio" value="replace" /> Replace all
            </label>
            <label class="flex items-center gap-1 text-xs text-n-slate-11">
              <input v-model="skillMode" type="radio" value="add" /> Add to existing
            </label>
            <button
              type="button"
              class="px-3 py-1 text-xs rounded-lg bg-n-blue-9 text-white hover:bg-n-blue-10 disabled:opacity-50"
              :disabled="skillUploading || !skillFile"
              @click="uploadSkills"
            >
              {{ skillUploading ? 'Uploading…' : 'Upload skills' }}
            </button>
          </div>
          <p
            v-if="skillStatus.state && skillStatus.state !== 'idle'"
            class="mt-2 text-xs"
            :class="skillStatus.state === 'error' ? 'text-n-ruby-9' : (skillStatus.state === 'done' ? 'text-n-teal-11' : 'text-n-slate-11')"
          >
            <span v-if="skillStatus.state === 'done'">✓ Applied — {{ skillStatus.docs }} skill file(s) active, agent reloaded.</span>
            <span v-else-if="skillStatus.state === 'error'">✗ {{ skillStatus.detail }}</span>
            <span v-else>⏳ {{ skillStatus.detail || skillStatus.state }}…</span>
          </p>
        </div>

        <!-- Default mode (applied to newly-selected inboxes; each inbox can override below) -->
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">Default mode</label>
          <div class="flex gap-2">
            <button
              v-for="m in sessionModes"
              :key="m"
              type="button"
              class="px-3 py-1.5 text-sm rounded-lg border capitalize"
              :class="
                form.session_mode === m
                  ? 'bg-n-blue-9 text-white border-n-blue-9'
                  : 'border-n-weak text-n-slate-12 hover:bg-n-alpha-2'
              "
              @click="form.session_mode = m"
            >
              {{ m === 'link' ? 'Web link (voice + screen)' : 'Phone call' }}
            </button>
          </div>
          <p class="mt-1 text-xs text-n-slate-11">
            Used as the default when you add an inbox below. Each inbox can be set to Link or Call individually.
          </p>
        </div>

        <!-- Inboxes (the gate) -->
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            Inboxes the AI works on <span class="text-n-ruby-9">*</span>
          </label>
          <div class="flex flex-col gap-2 p-3 rounded-lg border border-n-weak bg-n-solid-1 max-h-56 overflow-y-auto">
            <p v-if="!inboxes.length" class="text-xs text-n-slate-10">No inboxes found.</p>
            <div
              v-for="ib in inboxes"
              :key="ib.id"
              class="flex items-center justify-between gap-2 text-sm text-n-slate-12"
            >
              <label class="flex items-center gap-2">
                <input
                  type="checkbox"
                  :checked="form.inbox_ids.includes(ib.id)"
                  @change="toggleInbox(ib.id)"
                />
                {{ ib.name }}
              </label>
              <div v-if="form.inbox_ids.includes(ib.id)" class="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  class="px-2 py-0.5 text-xs rounded border"
                  :class="inboxMode(ib.id) === 'link'
                    ? 'bg-n-blue-9 text-white border-n-blue-9'
                    : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'"
                  @click="setInboxMode(ib.id, 'link')"
                >
                  Link
                </button>
                <button
                  type="button"
                  class="px-2 py-0.5 text-xs rounded border"
                  :class="inboxMode(ib.id) === 'call'
                    ? 'bg-n-blue-9 text-white border-n-blue-9'
                    : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'"
                  @click="setInboxMode(ib.id, 'call')"
                >
                  Call
                </button>
                <select
                  :value="inboxControl(ib.id)"
                  class="ml-1 px-1.5 py-0.5 text-xs rounded border border-n-weak bg-n-solid-1 text-n-slate-12"
                  title="How the AI may act on the screen"
                  @change="setInboxControl(ib.id, $event.target.value)"
                >
                  <option v-for="m in controlMethods" :key="m" :value="m">
                    {{ controlLabels[m] || m }}
                  </option>
                </select>
              </div>
            </div>
          </div>
          <p class="mt-1 text-xs text-n-slate-11">
            The AI only ever acts on conversations in these inboxes. <strong>Link</strong> = sends a
            join link; <strong>Call</strong> = AI phones the customer (phone bridge coming soon). The
            dropdown sets how the AI may act on the screen: <strong>Guide only</strong> (sees &amp;
            guides), <strong>Co-browse</strong> (drives a shared browser), or <strong>AnyDesk</strong>
            (controls the customer's PC).
          </p>
        </div>

        <!-- Status line -->
        <div
          class="text-sm rounded-lg px-3 py-2"
          :class="isActive ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'"
        >
          <template v-if="isActive">✓ Active — the AI will work on the selected inbox(es).</template>
          <template v-else>Inactive — enable it, pick an assistant, and select at least one inbox.</template>
        </div>

        <div class="flex justify-end pt-1 border-t border-n-weak">
          <button
            class="px-4 py-1.5 text-sm rounded-lg bg-n-blue-9 text-white hover:bg-n-blue-10 disabled:opacity-50"
            :disabled="saving"
            @click="save"
          >
            {{ saving ? 'Saving…' : 'Save' }}
          </button>
        </div>
      </div>
    </template>
  </div>
</template>
